import React from 'react';
import AppIcon from '../AppIcon';
import { Browsers, SmileyNervous, ArrowsSplit, PuzzlePiece } from '@phosphor-icons/react';
import { LayoutGrid, Workflow, AlertCircle, Wand2, Check } from 'lucide-react';

const problems = [
  { icon: Browsers, tone: 'sky', text: 'You’ve got seventeen browser tabs and somehow even less clarity.' },
  { icon: SmileyNervous, tone: 'coral', text: 'Pinterest gives you beautiful ideas and a tiny panic attack.' },
  { icon: ArrowsSplit, tone: 'lavender', text: 'You and your partner are quietly planning two different weddings.' },
  { icon: PuzzlePiece, tone: 'gold', text: 'Something feels missing. You just can’t put your finger on what.' },
];

const solutions = [
  'One quiet home for every wedding detail',
  'A plan that reshapes itself when life shifts',
  'Budget alerts that catch overspend before it lands',
  'Decisions that feel light, not loaded',
];

export default function ProblemSolution() {
  return (
    <section className="py-24 lg:py-32 bg-[#FBF7F2]">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <p className="section-eyebrow">Sound familiar?</p>
          <h2 className="font-serif text-4xl lg:text-5xl mt-3 text-[#2A2A2A] leading-tight">
            A lot of couples quietly feel <span className="italic text-[#7B2C3B]">like this</span>.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6 max-w-4xl mx-auto">
          {problems.map((p, i) => {
            return (
              <div
                key={i}
                className="group soft-card rounded-2xl p-5 flex items-start gap-4 hover:translate-y-[-2px] transition-transform"
              >
                <AppIcon icon={p.icon} tone={p.tone} size="md" interactive reveal delay={i * 80} />
                <p className="text-[#3A3A3A] leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-16 max-w-2xl mx-auto">
          <p className="font-serif text-2xl lg:text-3xl text-[#2A2A2A] italic">
            None of this is a planning problem.<br />
            It’s a <span className="not-italic font-medium text-[#7B2C3B]">missing structure</span> problem.
          </p>
          <div className="flex justify-center mt-6"><div className="divider-leaf" /></div>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="relative aspect-[4/3] rounded-[2rem] overflow-hidden soft-card">
            <img
              src="https://images.unsplash.com/photo-1520245787685-fb1e6732fb76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHx3ZWRkaW5nJTIwZGV0YWlsc3xlbnwwfHx8fDE3Nzk5MTIwNjZ8MA&ixlib=rb-4.1.0&q=85"
              alt="Wedding details"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="section-eyebrow">Which is exactly</p>
            <h3 className="font-serif text-3xl lg:text-4xl mt-3 text-[#2A2A2A]">
              Where Yourweddingfriend steps in.
            </h3>
            <ul className="mt-6 space-y-4">
              {solutions.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#7B2C3B] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-[#FBF7F2]" />
                  </div>
                  <span className="text-[#2A2A2A] text-lg">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
